import axios from 'axios';

import { addOrEditItem, API_ROOT, deleteItem, getItems } from '@/store/api';

export default {
    namespaced: true,
    actions: {
        addScenario: addOrEditItem('scenarios/', ''),
        getScenarios: getItems('scenarios/'),
        deleteScenario: deleteItem('scenarios/'),

        // Mint (or return the existing) share token for a scenario — PATCH
        // /<owner>/scenarios/<id>/share/. The payload stays exactly {id, user_id, action}:
        // addOrEditItem sends every other key as the request body.
        shareScenario({ dispatch }, { id, user_id }) {
            return dispatch('addScenario', { id, user_id, action: 'share' });
        },

        // Revoke a scenario's share token — PATCH .../scenarios/<id>/ with action: 'unshare'.
        // Mirrors shareScenario; the backend must support the 'unshare' action.
        unshareScenario({ dispatch }, { id, user_id }) {
            return dispatch('addScenario', { id, user_id, action: 'unshare' });
        },

        // Owner-only read of a scenario by its id (the direct calculator link). Returns the
        // scenario or false on 403/404 — deliberately NOT routed through logApiError, whose
        // 401/403 handling would log the user out just for opening someone else's link.
        async getScenario({ rootGetters }, id) {
            try {
                const userId = rootGetters.user?.uuid;
                if (!userId) return false;
                const { data } = await axios.get(`${API_ROOT}/${userId}/scenarios/${id}/`, rootGetters.axiosConfig);
                return data;
            } catch {
                return false;
            }
        },

        // The PDF of a report attached to a scenario (ENG-2918):
        // GET /<owner>/scenarios/<id>/reports/<report uuid>/. The SPA authenticates with an
        // HttpOnly cookie, so the bytes are fetched here rather than opened by URL, and
        // handed back as a Blob for the preview modal. Resolves null on failure — again
        // without logApiError, whose 401 handling would end the session over one PDF.
        async fetchScenarioReport({ rootGetters }, { id, user_id, reportId }) {
            try {
                const userId = user_id || rootGetters.user?.uuid;
                if (!userId || !id || !reportId) return null;
                const { data } = await axios.get(`${API_ROOT}/${userId}/scenarios/${id}/reports/${reportId}/`, {
                    ...rootGetters.axiosConfig,
                    responseType: 'blob',
                });
                return data;
            } catch {
                return null;
            }
        },

        // The scenario's report state and attached PDFs (ENG-2918), for polling while a
        // render runs on the report worker: GET /<owner>/scenarios/<id>/reports/. Unlike a
        // scenario GET this doesn't count as opening the run. Resolves { report_state,
        // reports } or null.
        async getScenarioReportStatus({ rootGetters }, { id, user_id }) {
            try {
                const userId = user_id || rootGetters.user?.uuid;
                if (!userId || !id) return null;
                const { data } = await axios.get(
                    `${API_ROOT}/${userId}/scenarios/${id}/reports/`,
                    rootGetters.axiosConfig,
                );
                return data;
            } catch {
                return null;
            }
        },

        // Start rendering the scenario's report from its saved payload (ENG-2918):
        // POST /<owner>/scenarios/<id>/reports/generate/. The backend queues it for the
        // report worker and answers 202 with the scenario reading `generating` (200 with
        // the finished report when it had to render inline), so callers poll
        // getScenarioReportStatus until the state settles. Resolves the refreshed scenario,
        // or null with the backend's reason (409 = this run can't have a report, 502 = an
        // inline render failed) — without logApiError, whose 401 handling would end the
        // session over one render.
        async generateScenarioReport({ rootGetters }, { id, user_id }) {
            try {
                const userId = user_id || rootGetters.user?.uuid;
                if (!userId || !id) return null;
                const { data } = await axios.post(
                    `${API_ROOT}/${userId}/scenarios/${id}/reports/generate/`,
                    {},
                    rootGetters.axiosConfig,
                );
                return data;
            } catch {
                return null;
            }
        },

        // Public share-by-token read. Works unauthenticated (AllowAny on the backend); the
        // auth header is sent only when logged in so the backend can resolve `can_edit` and
        // `owner_user_id` for the owner, creator, or advisor. Returns false on 404 / error
        // (again without logApiError's logout side-effect).
        async getSharedScenario({ rootGetters }, token) {
            try {
                const config = rootGetters.isLoggedIn ? rootGetters.axiosConfig : {};
                const { data } = await axios.get(`${API_ROOT}/sharedscenario/${token}/`, config);
                return data;
            } catch {
                return false;
            }
        },
    },
};
